import { QueryClient } from 'react-query';
import {
  CaseByIdQuery,
  CaseTeamMemberType,
  useMarkCaseAsReadMutation,
} from 'src/graphql';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { TRootState } from 'src/store';

type markCaseAsReadPayload = {
  caseId: string;
  userId: number;
  mutateMarkCaseAsRead: ReturnType<
    typeof useMarkCaseAsReadMutation
  >['mutateAsync'];
  queryClient: QueryClient;
};

export const markCaseAsRead = createAsyncThunk<
  void,
  markCaseAsReadPayload,
  { state: TRootState }
>(
  'case/markCaseAsRead',
  async (
    {
      caseId,
      userId,
      mutateMarkCaseAsRead,
      queryClient,
    }: markCaseAsReadPayload,
    _thunkAPI,
  ) => {
    if (!caseId || !userId) {
      return;
    }

    const caseQueryKey = ['CaseById', { id: caseId }];

    const getUpdatedCaseTeamMember = (
      user: CaseTeamMemberType,
      firstAccessedAt: string | null,
    ) => {
      if (user.id === userId) {
        return {
          ...user,
          firstAccessedAt,
        };
      }
      return user;
    };

    try {
      queryClient.cancelQueries(caseQueryKey);

      queryClient.setQueryData<CaseByIdQuery | undefined>(
        caseQueryKey,
        (oldData) => {
          if (!oldData?.caseById) return oldData;

          return {
            caseById: {
              ...oldData.caseById,
              CaseTeam: oldData.caseById.CaseTeam.map((user) =>
                getUpdatedCaseTeamMember(
                  user as CaseTeamMemberType,
                  new Date().toISOString(),
                ),
              ),
            },
          };
        },
      );

      await mutateMarkCaseAsRead({ caseId, userId });

      queryClient.invalidateQueries(caseQueryKey);
    } catch (error) {
      queryClient.setQueryData<CaseByIdQuery | undefined>(
        caseQueryKey,
        (oldData) => {
          if (!oldData?.caseById) return oldData;
          return {
            caseById: {
              ...oldData.caseById,
              CaseTeam: oldData.caseById.CaseTeam.map((user) =>
                getUpdatedCaseTeamMember(user as CaseTeamMemberType, null),
              ),
            },
          };
        },
      );
    }
  },
);
